/**
 * PackingOrdersModal - Modal de pedidos con productos incompletos
 *
 * Modal que muestra todos los pedidos que tienen productos pendientes
 * de empacar. Incluye:
 * - Lista de pedidos con avatar de tienda
 * - Productos incompletos por pedido con progreso
 * - Estado "Sin preparar" para todos los pedidos
 * - Funcionalidad de expandir/colapsar pedidos
 * - Botón de impresión por pedido
 * - Scroll con altura máxima para mejor UX
 */

import React, { forwardRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faPrint, faChevronDown, faChevronUp, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@shared/components/ui/shadcn/avatar';
import { usePackingContext } from '../../hooks';

interface OrderWithIncompleteProducts {
  id: string;
  ref: string;
  shop: {
    name: string;
    logo: string;
  };
  customer: {
    name: string;
  };
  incompleteProducts: Array<{
    id: string;
    name: string;
    code: string;
    image: string;
    quantity: number;
    expectedQuantity: number;
    processedQuantity: number;
  }>;
}

export const PackingOrdersModal = forwardRef<ModalCenterRef, Record<string, never>>((props, ref) => {
  const { t } = useTranslation('packingModule');
  const { orders, data } = usePackingContext();
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // Construir URL completa de imagen como en OrderForm.tsx
  const buildImageUrl = (imageHash: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
  };

  // Procesar órdenes y filtrar solo las que tienen productos incompletos
  const ordersWithIncompleteProducts = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    return orders
      .map((order: any) => {
        // Procesar productos del pedido
        const allProducts: any[] = [];
        const incompleteProducts: any[] = [];
        let totalExpected = 0;
        let totalProcessed = 0;

        if (order.lines) {
          order.lines.forEach((line: any) => {
            if (line.pack && line.pack.lines) {
              // Es un pack - verificar cada producto del pack
              line.pack.lines.forEach((packLine: any) => {
                const productCode = packLine.product?.code;
                const expectedQty = packLine.qty || 1;
                const processedQtyRaw = packLine.qty_done || 0; // Usar qty_done de la API
                const processedQty = order.status_id === 6 ? expectedQty : processedQtyRaw;
                totalExpected += expectedQty;
                totalProcessed += processedQty;

                const productObj = {
                  id: packLine.product_id?.toString() || 'unknown',
                  name: packLine.product?.name || 'Producto',
                  code: productCode || '',
                  image: buildImageUrl(packLine.product?.image_hash || ''),
                  expectedQuantity: expectedQty,
                  processedQuantity: processedQty,
                };
                allProducts.push(productObj);

                // Solo incluir si no está completo
                if (processedQty < expectedQty) {
                  incompleteProducts.push(productObj);
                }
              });
            } else if (line.product) {
              // Es un producto individual
              const productCode = line.product.code;
              const expectedQty = line.qty || 1;
              const processedQtyRaw = line.qty_done || 0; // Usar qty_done de la API
              const processedQty = order.status_id === 6 ? expectedQty : processedQtyRaw;
              totalExpected += expectedQty;
              totalProcessed += processedQty;

              const productObj = {
                id: line.product_id?.toString() || line.id?.toString(),
                name: line.name,
                code: productCode,
                image: buildImageUrl(line.product.image_hash || ''),
                expectedQuantity: expectedQty,
                processedQuantity: processedQty,
              };
              allProducts.push(productObj);

              // Solo incluir si no está completo
              if (processedQty < expectedQty) {
                incompleteProducts.push(productObj);
              }
            }
          });
        }

        // Siempre retornar la orden (completa o no) para listar TODAS
        return {
          id: order.id.toString(),
          ref: order.ref,
          shop: {
            name: order.shop?.name || '',
            logo: buildImageUrl(order.shop?.image_hash || ''),
          },
          customer: {
            name: order.customer?.name || '',
          },
          status_id: order.status_id,
          order_created_at: order.order_created_at,
          shipment: order.shipment,
          date_tracking_update: order.date_tracking_update,
          order_tracking_update: order.order_tracking_update,
          tracking_updated_at: order.tracking_updated_at,
          processed_at: order.processed_at,
          totalExpected,
          totalProcessed,
          products: allProducts,
          incompleteProducts,
        };
      })
      .filter((order): order is any => order !== null);
  }, [orders]);

  // Calcular estadísticas
  const totalOrders = orders?.length || 0;
  const completedOrders = orders?.filter((o: any) => o.status_id === 6).length || 0;

  // Handler para expandir/colapsar pedidos
  const toggleOrder = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  return (
    <ModalCenter
      ref={ref}
      title={t('packing_orders_modal.title')}
      type="default"
      width="lg"
      icon={<FontAwesomeIcon icon={faShoppingCart} className="w-4 h-4 text-icons" />}
    >
      <div className="space-y-6">
        {/* Header con progreso general */}
        <div className="flex items-center gap-2 mb-4">
          <div className="text-sm text-complementary-text">
            {t('packing_orders_modal.completed_summary', {
              completed: completedOrders,
              total: totalOrders,
            })}
          </div>
        </div>

        {/* Lista de pedidos con accordion */}
        <div className="space-y-0 max-h-96 overflow-y-auto">
          {ordersWithIncompleteProducts.map((order, index) => {
            if (!order) return null;
            return (
              <div
                key={order.id}
                className={`border-b border-lines ${
                  index === ordersWithIncompleteProducts.length - 1 ? 'border-b-0' : ''
                }`}
              >
                {/* Header del pedido */}
                <div className=" py-3">
                  <div className="flex items-center justify-between">
                    {/* Información del pedido */}
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="w-8 h-8 border border-card-border">
                        <AvatarImage src={order.shop?.logo || ''} alt={order.shop?.name || ''} />
                        <AvatarFallback className="bg-card-border text-xs font-medium">
                          {(order.shop?.name || '').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm font-medium text-title">{order.ref}</div>
                        <div className="text-xs text-subtext">{order.shop.name}</div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <FontAwesomeIcon icon={faPrint} className="!w-3 !h-3 text-icons" />
                      </Button>
                      <button
                        className="p-2 hover:bg-hover-press rounded transition-colors"
                        onClick={() => toggleOrder(order.id)}
                        data-testid={`order-toggle-${order.id}`}
                      >
                        <FontAwesomeIcon
                          icon={expandedOrders.has(order.id) ? faChevronUp : faChevronDown}
                          className="w-3.5 h-3.5 text-icons"
                        />
                      </button>
                    </div>
                  </div>
                  {/* Badge de estado - Fecha cuando está preparada, sino "Sin preparar" */}
                  <div className="mt-1">
                    {order.status_id === 6 ? (
                      <Badge
                        variant="outline"
                        className="text-xs inline-flex items-center gap-2 border-0 bg-card-border light:bg-bg-success text-success"
                      >
                        <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                        {(() => {
                          const dateToShow =
                            order?.processed_at ||
                            order?.shipment?.last_shipment_update ||
                            order?.order_tracking_update ||
                            order?.date_tracking_update ||
                            order?.tracking_updated_at ||
                            order?.order_created_at;
                          return dateToShow
                            ? new Date(dateToShow).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : t('packing_orders_modal.status_prepared');
                        })()}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-xs inline-flex items-center gap-2 border-0 bg-card-border text-badge-status-3"
                      >
                        <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" />
                        {t('packing_orders_modal.status_not_prepared')}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Contenido expandible - Detalle del pedido */}
                {expandedOrders.has(order.id) && (
                  <div className="pb-3 space-y-3">
                    {(order.products || []).map((product, productIndex) => (
                      <div
                        key={product.id}
                        className={`flex items-center justify-between ${
                          productIndex < (order.products || []).length - 1 ? 'pb-3 border-b border-lines' : ''
                        }`}
                      >
                        {/* Información del producto */}
                        <div className="flex items-center gap-3 flex-1">
                          <Product
                            id={parseInt(product.id)}
                            name={product.name}
                            src={product.image}
                            code={product.code}
                          />
                        </div>

                        {/* Progreso de cantidad del producto */}
                        <div className="text-xs font-medium text-title bg-card-border rounded-full px-3 py-1">
                          {product.processedQuantity} / {product.expectedQuantity}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mensaje cuando no hay pedidos */}
        {ordersWithIncompleteProducts.length === 0 && (
          <div className="text-center py-8">
            <p className="text-complementary-text text-xs">{t('packing_orders_modal.no_orders')}</p>
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

PackingOrdersModal.displayName = 'PackingOrdersModal';
